import type { ChangeEvent, FocusEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePagination } from 'react-instantsearch';
import { useDebounce } from 'react-use';

import type { RangeValues } from '@sorare/core/src/atoms/inputs/RangeSlider';
import { RangeSlider as RangeSliderAtom } from '@sorare/core/src/atoms/inputs/RangeSlider';
import type { NonNullableRangeInput } from '@sorare/core/src/components/compose/types';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import {
  IconWrapper,
  InputsLine,
  Separator,
  StyledInput,
  StyledLabel,
} from '@sorare/core/src/components/search/RangeSliderWithInputs';
import {
  useBackendPagination,
  useBackendRangeSlider,
} from '@sorare/core/src/contexts/graphql-search';
import { useRangeSlider } from '@sorare/core/src/hooks/useRangeSlider';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { clamp } from '@sorare/core/src/lib/math';

interface Props {
  attribute: string;
  minForced?: number;
  maxForced?: number;
  algorithm?: {
    scale: (x: number) => number;
    unscale: (x: number) => number;
  };
  fromInput?: (value: number) => number;
  toInput?: (value: number) => string;
  greaterThanMode?: boolean;
  icon?: ReactNode;
  minIcon?: ReactNode;
  maxIcon?: ReactNode;
  visible: boolean;
  disabled?: boolean;
  onRefine?: (value: NonNullableRangeInput) => void;
}

const DEFAULT_ALGORITHM = {
  scale: (x: number) => x,
  unscale: (x: number) => x,
};

const defaultFromInputFn = (value: number) => value;
const defaultToInputFn = (value: number) => value.toString();

// Notes around the usage of this slider:
// For setting hardcoded minimum and maximum, avoid using min and max (these are provided automatically by Algolia)
// If you want to set them, use minForced and maxForced.
// Explanation: the reason for this is that by setting min and max, we will force new extra disjunctive queries at
// every page load that are counted as extra queries
// We are not using the scale props of the Slider component because it actually makes it more complicated in terms of transformations. We are handling the scaling ourselves with the algorithm props by scaling the value we receive from the Slider.
export const RangeSlider = ({
  attribute,
  minForced,
  maxForced,
  algorithm = DEFAULT_ALGORITHM,
  fromInput = defaultFromInputFn,
  toInput = defaultToInputFn,
  greaterThanMode = false,
  icon,
  minIcon = icon,
  maxIcon = icon,
  visible,
  disabled: disabledProp,
  onRefine,
}: Props) => {
  const {
    range: { max: realMax },
  } = useRangeSlider({
    attribute,
  });

  const needGreaterThanMode =
    !!greaterThanMode &&
    realMax !== undefined &&
    maxForced !== undefined &&
    realMax > maxForced;

  const actualMaxForced =
    greaterThanMode && !needGreaterThanMode ? undefined : maxForced;

  const {
    refine,
    canRefine,
    range: { min, max },
    start: [currentRefinementMin, currentRefinementMax],
  } = useRangeSlider({
    attribute,
    // Forcing the min/max requires a work-around to not trigger a refinement (impacts URL & numericFilters param).
    // See InstantSearch/index.tsx & packages/marketplace/src/searchCards/RangeSlider/index.tsx
    min: minForced,
    max: greaterThanMode ? undefined : maxForced,
  });

  const minimum = minForced ?? (min || 0);
  const maximum = actualMaxForced ?? (max || 0);

  const localCurrentRefinementMin =
    currentRefinementMin && currentRefinementMin === -Infinity
      ? minimum || 0
      : currentRefinementMin || 0;

  const localCurrentRefinementMax =
    currentRefinementMax && currentRefinementMax === Infinity
      ? maximum || 0
      : currentRefinementMax || 0;

  const [currentValues, setCurrentValues] = useState<RangeValues>({
    low: algorithm.unscale(localCurrentRefinementMin || minimum),
    high: algorithm.unscale(localCurrentRefinementMax || maximum),
  });

  const [inputValues, setInputValues] = useState<{ low: string; high: string }>(
    {
      low: toInput(algorithm.scale(currentValues.low)),
      high: toInput(algorithm.scale(currentValues.high)),
    }
  );

  const [editingInputs, setEditingInputs] = useState(false);

  const track = useEvents();
  const { refine: setPage } = usePagination();

  const { sliderMin, sliderMax } = useMemo(() => {
    return {
      sliderMin: algorithm.unscale(minimum),
      sliderMax: algorithm.unscale(maximum),
    };
  }, [algorithm, maximum, minimum]);

  const reachEnd = currentValues.high === sliderMax;

  const refineValues = useCallback(() => {
    if (
      localCurrentRefinementMin !== currentValues.low ||
      localCurrentRefinementMax !== currentValues.high
    ) {
      const newMin = Math.max(
        Math.round(algorithm.scale(currentValues.low!)),
        minimum!
      );
      const newMax = Math.min(
        Math.round(algorithm.scale(currentValues.high!)),
        maximum!
      );

      track('Use Market Filter', {
        filterName: attribute,
        filterValue: `${Math.floor(newMin)}-${Math.ceil(newMax)}`,
      });
      setPage(0);

      const actualMax = needGreaterThanMode && reachEnd ? max : newMax;
      refine([newMin, needGreaterThanMode && reachEnd ? max : newMax]);
      onRefine?.({ min: newMin, max: Number(actualMax) });
    }
  }, [
    localCurrentRefinementMin,
    currentValues.low,
    currentValues.high,
    localCurrentRefinementMax,
    algorithm,
    minimum,
    maximum,
    track,
    attribute,
    setPage,
    needGreaterThanMode,
    reachEnd,
    max,
    refine,
    onRefine,
  ]);

  const changeInputValues = (range: { low?: string; high?: string }) => {
    setInputValues(prev => ({ ...prev, ...range }));
  };

  const changeSliderValues = useCallback(
    (range: { low?: number; high?: number }, updateInputValues?: boolean) => {
      if (
        range.high !== undefined &&
        Math.round(range.high) === Math.round(sliderMax)
      ) {
        range.high = sliderMax;
      }

      const clampedLow =
        range.low !== undefined
          ? clamp(range.low, sliderMin, sliderMax)
          : undefined;
      const clampedHigh =
        range.high !== undefined
          ? clamp(range.high, sliderMin, sliderMax)
          : undefined;

      setCurrentValues(prev => ({
        ...prev,
        ...(clampedLow !== undefined && { low: clampedLow }),
        ...(clampedHigh !== undefined && { high: clampedHigh }),
      }));

      if (updateInputValues) {
        changeInputValues({
          ...(clampedLow !== undefined && {
            low: toInput(algorithm.scale(clampedLow)),
          }),
          ...(clampedHigh !== undefined && {
            high: toInput(algorithm.scale(clampedHigh)),
          }),
        });
      }
    },
    [algorithm, toInput, sliderMin, sliderMax]
  );

  // // the `currentRefinement` probably changed because of `Clear all filter`
  // // we need to reset the `currentValues` to their initial state
  useEffect(() => {
    if (canRefine) {
      changeSliderValues(
        {
          low: algorithm.unscale(localCurrentRefinementMin || minimum),
          high: algorithm.unscale(localCurrentRefinementMax || maximum),
        },
        !editingInputs
      );
    }
  }, [
    canRefine,
    localCurrentRefinementMin,
    localCurrentRefinementMax,
    algorithm,
    minimum,
    maximum,
    changeSliderValues,
    editingInputs,
  ]);

  useDebounce(
    () => {
      if (editingInputs) {
        refineValues();
      }
    },
    200,
    [inputValues]
  );

  // If we don't have results, and we haven't forced a min/max on the slider, then don't show anything
  if (
    (min === undefined || max === undefined) &&
    !minForced &&
    !actualMaxForced
  ) {
    return null;
  }

  const disabled = disabledProp || minimum === maximum;
  const shouldDisplayPlus = needGreaterThanMode && reachEnd;

  if (!visible) return <FilterSection hidden={disabled} />;

  return (
    <FilterSection hidden={disabled}>
      <RangeSliderAtom
        rangeValues={{
          low: currentValues.low,
          high: currentValues.high,
        }}
        min={sliderMin}
        max={sliderMax}
        disabled={disabled}
        onChange={(range: RangeValues) => {
          changeSliderValues(
            {
              low: range.low,
              high: range.high,
            },
            true
          );
        }}
        onMouseUp={refineValues}
      />
      <InputsLine>
        <StyledLabel>
          {!!minIcon && <IconWrapper>{minIcon}</IconWrapper>}
          <StyledInput
            id="a-input1"
            name={attribute}
            type="number"
            min={toInput(algorithm.scale(sliderMin))}
            max={toInput(algorithm.scale(sliderMax))}
            value={inputValues.low}
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setEditingInputs(false);
              changeSliderValues(
                {
                  low: algorithm.unscale(fromInput(+event.target.value)),
                },
                true
              );
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              changeInputValues({
                low: event.target.value,
              });
              changeSliderValues({
                low: algorithm.unscale(fromInput(+event.target.value)),
              });
            }}
            onFocus={() => setEditingInputs(true)}
            withIcon={!!minIcon}
            withMinMax={typeof max === 'number'}
            borderColor="transparent"
          />
        </StyledLabel>
        <Separator>-</Separator>
        <StyledLabel>
          {!!maxIcon && <IconWrapper>{maxIcon}</IconWrapper>}
          <StyledInput
            id="b-input2"
            name={attribute}
            type="number"
            min={toInput(algorithm.scale(sliderMin))}
            max={toInput(algorithm.scale(sliderMax))}
            value={shouldDisplayPlus ? '' : inputValues.high}
            placeholder={
              shouldDisplayPlus
                ? `${+toInput(algorithm.scale(sliderMax))}+`
                : undefined
            }
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setEditingInputs(false);
              changeSliderValues(
                {
                  high: algorithm.unscale(fromInput(+event.target.value)),
                },
                true
              );
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              changeInputValues({
                high: event.target.value,
              });
              changeSliderValues({
                high: algorithm.unscale(fromInput(+event.target.value)),
              });
            }}
            onFocus={() => setEditingInputs(true)}
            withIcon={!!maxIcon}
            withMinMax={typeof min === 'number'}
            borderColor="transparent"
          />
        </StyledLabel>
      </InputsLine>
    </FilterSection>
  );
};

export const BackendRangeSlider = ({
  attribute,
  minForced,
  maxForced,
  algorithm = DEFAULT_ALGORITHM,
  fromInput = defaultFromInputFn,
  toInput = defaultToInputFn,
  greaterThanMode = false,
  icon,
  minIcon = icon,
  maxIcon = icon,
  visible,
  disabled: disabledProp,
  onRefine,
}: Props) => {
  const {
    range: { max: realMax },
  } = useBackendRangeSlider({
    attribute,
  });

  const needGreaterThanMode =
    !!greaterThanMode &&
    realMax !== undefined &&
    maxForced !== undefined &&
    realMax > maxForced;

  const actualMaxForced =
    greaterThanMode && !needGreaterThanMode ? undefined : maxForced;

  const {
    refine,
    canRefine,
    range: { min, max },
    start: [currentRefinementMin, currentRefinementMax],
  } = useBackendRangeSlider({
    attribute,
    // Forcing the min/max requires a work-around to not trigger a refinement (impacts URL & numericFilters param).
    // See InstantSearch/index.tsx & packages/marketplace/src/searchCards/RangeSlider/index.tsx
    min: minForced,
    max: greaterThanMode ? undefined : maxForced,
  });

  const minimum = minForced ?? (min || 0);
  const maximum = actualMaxForced ?? (max || 0);

  const localCurrentRefinementMin =
    currentRefinementMin && currentRefinementMin === -Infinity
      ? minimum || 0
      : currentRefinementMin || 0;

  const localCurrentRefinementMax =
    currentRefinementMax && currentRefinementMax === Infinity
      ? maximum || 0
      : currentRefinementMax || 0;

  const [currentValues, setCurrentValues] = useState<RangeValues>({
    low: algorithm.unscale(localCurrentRefinementMin || minimum),
    high: algorithm.unscale(localCurrentRefinementMax || maximum),
  });

  const [inputValues, setInputValues] = useState<{ low: string; high: string }>(
    {
      low: toInput(algorithm.scale(currentValues.low)),
      high: toInput(algorithm.scale(currentValues.high)),
    }
  );

  const [editingInputs, setEditingInputs] = useState(false);

  const track = useEvents();
  const { refine: setPage } = useBackendPagination();

  const { sliderMin, sliderMax } = useMemo(() => {
    return {
      sliderMin: algorithm.unscale(minimum),
      sliderMax: algorithm.unscale(maximum),
    };
  }, [algorithm, maximum, minimum]);

  const reachEnd = currentValues.high === sliderMax;

  const refineValues = useCallback(() => {
    if (
      localCurrentRefinementMin !== currentValues.low ||
      localCurrentRefinementMax !== currentValues.high
    ) {
      const newMin = Math.max(
        Math.round(algorithm.scale(currentValues.low!)),
        minimum!
      );
      const newMax = Math.min(
        Math.round(algorithm.scale(currentValues.high!)),
        maximum!
      );

      track('Use Market Filter', {
        filterName: attribute,
        filterValue: `${Math.floor(newMin)}-${Math.ceil(newMax)}`,
      });
      setPage(0);

      const actualMax = needGreaterThanMode && reachEnd ? max : newMax;
      refine([newMin, needGreaterThanMode && reachEnd ? max : newMax]);
      onRefine?.({ min: newMin, max: Number(actualMax) });
    }
  }, [
    localCurrentRefinementMin,
    currentValues.low,
    currentValues.high,
    localCurrentRefinementMax,
    algorithm,
    minimum,
    maximum,
    track,
    attribute,
    setPage,
    needGreaterThanMode,
    reachEnd,
    max,
    refine,
    onRefine,
  ]);

  const changeInputValues = (range: { low?: string; high?: string }) => {
    setInputValues(prev => ({ ...prev, ...range }));
  };

  const changeSliderValues = useCallback(
    (range: { low?: number; high?: number }, updateInputValues?: boolean) => {
      if (
        range.high !== undefined &&
        Math.round(range.high) === Math.round(sliderMax)
      ) {
        range.high = sliderMax;
      }

      const clampedLow =
        range.low !== undefined
          ? clamp(range.low, sliderMin, sliderMax)
          : undefined;
      const clampedHigh =
        range.high !== undefined
          ? clamp(range.high, sliderMin, sliderMax)
          : undefined;

      setCurrentValues(prev => ({
        ...prev,
        ...(clampedLow !== undefined && { low: clampedLow }),
        ...(clampedHigh !== undefined && { high: clampedHigh }),
      }));

      if (updateInputValues) {
        changeInputValues({
          ...(clampedLow !== undefined && {
            low: toInput(algorithm.scale(clampedLow)),
          }),
          ...(clampedHigh !== undefined && {
            high: toInput(algorithm.scale(clampedHigh)),
          }),
        });
      }
    },
    [algorithm, toInput, sliderMin, sliderMax]
  );

  // // the `currentRefinement` probably changed because of `Clear all filter`
  // // we need to reset the `currentValues` to their initial state
  useEffect(() => {
    if (canRefine) {
      changeSliderValues(
        {
          low: algorithm.unscale(localCurrentRefinementMin || minimum),
          high: algorithm.unscale(localCurrentRefinementMax || maximum),
        },
        !editingInputs
      );
    }
  }, [
    canRefine,
    localCurrentRefinementMin,
    localCurrentRefinementMax,
    algorithm,
    minimum,
    maximum,
    changeSliderValues,
    editingInputs,
  ]);

  useDebounce(
    () => {
      if (editingInputs) {
        refineValues();
      }
    },
    200,
    [inputValues]
  );

  // If we don't have results, and we haven't forced a min/max on the slider, then don't show anything
  if (
    (min === undefined || max === undefined) &&
    !minForced &&
    !actualMaxForced
  ) {
    return null;
  }

  const disabled = disabledProp || minimum === maximum;
  const shouldDisplayPlus = needGreaterThanMode && reachEnd;

  if (!visible) return <FilterSection hidden={disabled} />;

  return (
    <FilterSection hidden={disabled}>
      <RangeSliderAtom
        rangeValues={{
          low: currentValues.low,
          high: currentValues.high,
        }}
        min={sliderMin}
        max={sliderMax}
        disabled={disabled}
        onChange={(range: RangeValues) => {
          changeSliderValues(
            {
              low: range.low,
              high: range.high,
            },
            true
          );
        }}
        onMouseUp={refineValues}
      />
      <InputsLine>
        <StyledLabel>
          {!!minIcon && <IconWrapper>{minIcon}</IconWrapper>}
          <StyledInput
            id="a-input1"
            name={attribute}
            type="number"
            min={toInput(algorithm.scale(sliderMin))}
            max={toInput(algorithm.scale(sliderMax))}
            value={inputValues.low}
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setEditingInputs(false);
              changeSliderValues(
                {
                  low: algorithm.unscale(fromInput(+event.target.value)),
                },
                true
              );
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              changeInputValues({
                low: event.target.value,
              });
              changeSliderValues({
                low: algorithm.unscale(fromInput(+event.target.value)),
              });
            }}
            onFocus={() => setEditingInputs(true)}
            withIcon={!!minIcon}
            withMinMax={typeof max === 'number'}
            borderColor="transparent"
          />
        </StyledLabel>
        <Separator>-</Separator>
        <StyledLabel>
          {!!maxIcon && <IconWrapper>{maxIcon}</IconWrapper>}
          <StyledInput
            id="b-input2"
            name={attribute}
            type="number"
            min={toInput(algorithm.scale(sliderMin))}
            max={toInput(algorithm.scale(sliderMax))}
            value={shouldDisplayPlus ? '' : inputValues.high}
            placeholder={
              shouldDisplayPlus
                ? `${+toInput(algorithm.scale(sliderMax))}+`
                : undefined
            }
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setEditingInputs(false);
              changeSliderValues(
                {
                  high: algorithm.unscale(fromInput(+event.target.value)),
                },
                true
              );
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              changeInputValues({
                high: event.target.value,
              });
              changeSliderValues({
                high: algorithm.unscale(fromInput(+event.target.value)),
              });
            }}
            onFocus={() => setEditingInputs(true)}
            withIcon={!!maxIcon}
            withMinMax={typeof min === 'number'}
            borderColor="transparent"
          />
        </StyledLabel>
      </InputsLine>
    </FilterSection>
  );
};
